import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { educateApi, EducateApiError, type SaveProgressInput } from '@/services/educateApi';
import { env } from '@/config/env';
import { appendPreviewId, isPreviewMode } from '@/utils/previewUtils';
import { buildUnits, isBranching, type EducateUnit } from '@/utils/units';
import {
  clearPlayerProgress,
  contentSignature,
  loadPlayerProgress,
  reconcileContentSignature
} from '@/utils/playerProgress';
import type {
  DecisionTreeContent,
  EducateStoryline,
  EducateInteraction,
  EducateProgress
} from '@/types/educate';

export type Phase = 'loading' | 'error' | 'welcome' | 'module' | 'complete';

interface State {
  phase: Phase;
  error: string | null;
  storyline: EducateStoryline | null;
  progress: EducateProgress | null;
  currentUnitId: string | null;
  completedUnits: string[];
  interactions: EducateInteraction[];
}

const INITIAL: State = {
  phase: 'loading',
  error: null,
  storyline: null,
  progress: null,
  currentUnitId: null,
  completedUnits: [],
  interactions: []
};

export function useEducateModule() {
  const [state, setState] = useState<State>(INITIAL);

  const startedAt = useRef<number>(Date.now());
  const completing = useRef(false);

  const units = useMemo<EducateUnit[]>(
    () => (state.storyline ? buildUnits(state.storyline.educateType, state.storyline.content) : []),
    [state.storyline]
  );

  const currentIndex = useMemo(
    () => units.findIndex((unit) => unit.id === state.currentUnitId),
    [units, state.currentUnitId]
  );

  const currentUnit = currentIndex >= 0 ? units[currentIndex] : null;

  const totalUnits = state.storyline?.totalUnits || units.length;

  useEffect(() => {
    let cancelled = false;

    educateApi
      .bootstrap()
      .then(({ storyline, progress }) => {
        if (cancelled) return;

        const built = buildUnits(storyline.educateType, storyline.content);
        const done = progress?.progress?.completedUnits ?? [];

        const branching = isBranching(storyline.educateType);

        // If the module was republished from the editor, its content changed — drop any stale
        // saved player progress so the learner starts from the welcome/start page again.
        const contentChanged = reconcileContentSignature(
          storyline.storylineId,
          contentSignature(storyline.content)
        );

        const hasLocalProgress =
          !contentChanged && Object.keys(loadPlayerProgress(storyline.storylineId)).length > 0;

        const savedUnitId = progress?.progress?.currentUnitId ?? null;
        let resumeUnitId: string | null;
        if (!contentChanged && savedUnitId && built.some((unit) => unit.id === savedUnitId)) {
          resumeUnitId = savedUnitId;
        } else if (branching) {
          const rootId = `node-${(storyline.content as DecisionTreeContent).startNodeId}`;
          resumeUnitId = built.some((unit) => unit.id === rootId) ? rootId : (built[0]?.id ?? null);
        } else {
          const resumeIndex = built.findIndex((unit) => !done.includes(unit.id));
          resumeUnitId = built[resumeIndex === -1 ? 0 : resumeIndex]?.id ?? null;
        }

        // Branching modules "complete" the moment an outcome is reached, but the learner
        // stays on the outcome and can explore other paths — so on refresh resume into the
        // player (which restores the reached outcome) rather than the completion screen.
        // Self-contained players (e.g. diagnostic) never mark the backend IN_PROGRESS, so a
        // saved local snapshot is also a signal to resume into the module.
        // If content changed since last load (republished), always return to the welcome screen
        // regardless of any saved backend progress — the old position is stale.
        const resumePhase: Phase = contentChanged
          ? 'welcome'
          : progress?.status === 'COMPLETED'
            ? branching
              ? 'module'
              : 'complete'
            : progress?.status === 'IN_PROGRESS' || hasLocalProgress
              ? 'module'
              : 'welcome';

        setState({
          phase: resumePhase,
          error: null,
          storyline,
          progress,
          currentUnitId: contentChanged ? (built[0]?.id ?? null) : resumeUnitId,
          completedUnits: contentChanged ? [] : done,
          interactions: contentChanged ? [] : (progress?.interactions ?? [])
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('[educate] bootstrap failed', {
          error,
          hadPreviewId: isPreviewMode(),
          bootstrapUrl: appendPreviewId(env.educate.bootstrapUrl),
          location: window.location.href
        });
        setState({
          ...INITIAL,
          phase: 'error',
          error:
            error instanceof EducateApiError
              ? `${error.message} (HTTP ${error.status})`
              : error instanceof Error
                ? error.message
                : 'Could not load this Educate module.'
        });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const elapsedSeconds = () => Math.round((Date.now() - startedAt.current) / 1000);

  const persist = useCallback(
    (input: Omit<SaveProgressInput, 'totalUnits' | 'timeSpentSeconds'>) => {
      const payload: SaveProgressInput = {
        ...input,
        totalUnits,
        timeSpentSeconds: elapsedSeconds()
      };

      educateApi
        .saveProgress(payload)
        .then((progress) => setState((prev) => ({ ...prev, progress })))
        .catch((error: unknown) => console.warn('[educate] progress save failed', error));
    },
    [totalUnits]
  );

  const start = useCallback(() => {
    startedAt.current = Date.now();
    educateApi.markStarted();
    if (isPreviewMode()) {
      persist({
        currentIndex: Math.max(currentIndex, 0),
        currentUnitId: state.currentUnitId,
        completedUnits: state.completedUnits
      });
    }
    setState((prev) => ({ ...prev, phase: 'module' }));
  }, [persist, currentIndex, state.currentUnitId, state.completedUnits]);

  const finish = useCallback(
    (completedUnits: string[], interactions: EducateInteraction[]) => {
      if (completing.current) return;
      completing.current = true;

      setState((prev) => ({ ...prev, phase: 'complete' }));

      educateApi
        .complete({
          currentIndex: Math.max(units.length - 1, 0),
          totalUnits,
          completedUnits,
          interactions,
          timeSpentSeconds: elapsedSeconds()
        })
        .then((progress) => {
          setState((prev) => ({ ...prev, progress }));
          educateApi.markCompleted();
        })
        .catch((error: unknown) => {
          completing.current = false;
          setState((prev) => ({
            ...prev,
            phase: 'error',
            error:
              error instanceof Error
                ? `Your progress could not be recorded: ${error.message}`
                : 'Your progress could not be recorded.'
          }));
        });
    },
    [units.length, totalUnits]
  );

  const completeInPlace = useCallback(
    (completedUnits: string[], interactions: EducateInteraction[]) => {
      if (completing.current) return;
      completing.current = true;

      educateApi
        .complete({
          currentIndex: Math.max(units.length - 1, 0),
          totalUnits,
          completedUnits,
          interactions,
          timeSpentSeconds: elapsedSeconds()
        })
        .then((progress) => {
          setState((prev) => ({ ...prev, progress }));
          educateApi.markCompleted();
        })
        .catch(() => {
          completing.current = false;
        });
    },
    [units.length, totalUnits]
  );

  const finishInPlace = useCallback(() => {
    setState((prev) => ({ ...prev, phase: 'complete' }));
  }, []);

  const advance = useCallback(
    (interactions: EducateInteraction[] = [], nextUnitId?: string | null) => {
      const { currentUnitId } = state;
      if (!currentUnitId) return;

      const completedUnits = [...new Set([...state.completedUnits, currentUnitId])];

      const byUnit = new Map(state.interactions.map((i) => [i.unitId, i]));
      for (const interaction of interactions) byUnit.set(interaction.unitId, interaction);
      const merged = [...byUnit.values()];

      const index = units.findIndex((unit) => unit.id === currentUnitId);
      const target = nextUnitId !== undefined ? nextUnitId : (units[index + 1]?.id ?? null);

      setState((prev) => ({ ...prev, completedUnits, interactions: merged }));

      if (target === null) {
        finish(completedUnits, merged);
        return;
      }

      setState((prev) => ({ ...prev, currentUnitId: target }));
      persist({ currentIndex: index + 1, currentUnitId: target, completedUnits, interactions });
    },
    [state, units, persist, finish]
  );

  const setCurrentPosition = useCallback(
    (index: number, unitId: string) => {
      if (!units.some((unit) => unit.id === unitId)) return;
      setState((prev) => ({ ...prev, currentUnitId: unitId }));
      persist({
        currentIndex: index,
        currentUnitId: unitId,
        completedUnits: state.completedUnits,
        interactions: state.interactions
      });
    },
    [units, persist, state]
  );

  const goBack = useCallback(() => {
    setState((prev) => {
      if (prev.storyline && isBranching(prev.storyline.educateType)) return prev;

      const index = units.findIndex((unit) => unit.id === prev.currentUnitId);
      if (index <= 0) return prev;
      return { ...prev, currentUnitId: units[index - 1].id };
    });
  }, [units]);

  const goTo = useCallback(
    (unitId: string) => {
      setState((prev) => {
        if (prev.storyline && isBranching(prev.storyline.educateType)) return prev;
        if (!units.some((unit) => unit.id === unitId)) return prev;
        return { ...prev, currentUnitId: unitId };
      });
    },
    [units]
  );

  const restart = useCallback(() => {
    completing.current = false;
    startedAt.current = Date.now();
    clearPlayerProgress(state.storyline?.storylineId);

    const first = state.storyline
      ? isBranching(state.storyline.educateType)
        ? `node-${(state.storyline.content as DecisionTreeContent).startNodeId}`
        : (units[0]?.id ?? null)
      : null;

    setState((prev) => ({
      ...prev,
      phase: 'welcome',
      progress: null,
      currentUnitId: first,
      completedUnits: [],
      interactions: []
    }));

    educateApi
      .reset()
      .then((progress) => setState((prev) => ({ ...prev, progress })))
      .catch((error: unknown) => console.warn('[educate] progress reset failed', error));
  }, [state.storyline, units]);

  return {
    ...state,
    units,
    currentUnit,
    currentIndex,
    totalUnits,
    isFirstUnit: currentIndex <= 0,
    isLastUnit: currentIndex === units.length - 1,
    percentComplete:
      totalUnits > 0
        ? Math.min(100, Math.round((state.completedUnits.length / totalUnits) * 100))
        : 0,
    start,
    advance,
    setCurrentPosition,
    completeInPlace,
    finishInPlace,
    finishModule: finish,
    goBack,
    goTo,
    restart
  };
}

export type EducateModule = ReturnType<typeof useEducateModule>;
